<template>
    <button
        v-if="toggleOnly && canViewCost"
        type="button"
        class="inline-flex items-center justify-center rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
        :title="isCostVisible ? 'Hide cost' : 'Reveal cost'"
        :aria-label="isCostVisible ? 'Hide cost' : 'Reveal cost'"
        :aria-pressed="isCostVisible"
        @click.stop="auth.toggleCostReveal()"
    >
        <EyeSlashIcon v-if="isCostVisible" :class="iconClass" aria-hidden="true" />
        <EyeIcon v-else :class="iconClass" aria-hidden="true" />
        <Tooltip :text="isCostVisible ? 'Hide cost' : 'Reveal cost'" font="small" />
    </button>

    <span
        v-else-if="canViewCost"
        class="inline-flex items-center gap-1"
        :class="wrapperClass"
    >
        <span v-if="isCostVisible" :class="valueClass">
            <slot>{{ displayValue }}</slot>
        </span>
        <span
            v-else
            class="select-none tracking-widest text-gray-400"
            :class="valueClass"
            aria-hidden="true"
        >••••</span>
        <button
            v-if="showToggle"
            type="button"
            class="inline-flex items-center justify-center rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
            :title="isCostVisible ? 'Hide cost' : 'Reveal cost'"
            :aria-label="isCostVisible ? 'Hide cost' : 'Reveal cost'"
            :aria-pressed="isCostVisible"
            @click.stop="auth.toggleCostReveal()"
        >
            <EyeSlashIcon v-if="isCostVisible" :class="iconClass" aria-hidden="true" />
            <EyeIcon v-else :class="iconClass" aria-hidden="true" />
            <Tooltip :text="isCostVisible ? 'Hide cost' : 'Reveal cost'" font="small" />
        </button>
    </span>
</template>

<script setup>
import { computed } from 'vue';
import { EyeIcon, EyeSlashIcon } from '@heroicons/vue/24/outline';
import { useAuthStore } from '@/stores/auth-store.js';
import Tooltip from '@/components/Tooltip.vue';

const props = defineProps({
    /** When true, render only the eye toggle (for column / section headers). */
    toggleOnly: {
        type: Boolean,
        default: false,
    },
    /** Show the eye next to the value. Set false in dense table cells when the header has the toggle. */
    showToggle: {
        type: Boolean,
        default: true,
    },
    /** Raw value to display when revealed (ignored when a default slot is provided). */
    value: {
        type: [String, Number, null],
        default: null,
    },
    /** Optional formatter for `value`. */
    format: {
        type: Function,
        default: null,
    },
    fallback: {
        type: String,
        default: 'N/A',
    },
    iconClass: {
        type: String,
        default: 'h-4 w-4',
    },
    valueClass: {
        type: String,
        default: '',
    },
    wrapperClass: {
        type: String,
        default: '',
    },
});

const auth = useAuthStore();
const canViewCost = computed(() => auth.canViewCost());
const isCostVisible = computed(() => auth.isCostVisible());

const displayValue = computed(() => {
    if (props.value == null || props.value === '') {
        return props.fallback;
    }
    if (typeof props.format === 'function') {
        return props.format(props.value);
    }
    return props.value;
});
</script>
